import { inject, Injectable } from '@angular/core';
import { catchError, filter, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { PageModel } from '../model/page-model.model';
import { Treatment } from '../model/treatment';
import { HttpClient, HttpEventType, HttpParams, HttpParamsOptions, HttpResponse } from '@angular/common/http';
import { ListPagedTreatmentQuery } from '../model/ListPagedTreatmentQuery';
import {
    AiCadLaunchSession,
    RetainerAiJobSubmission,
    ItemsCount,
    RevisionInsights,
    TreatmentDetail,
    TreatmentTag,
    GetPatientRelatedTreatmentsRequest,
    PatientTreatmentRecord,
} from '../model';
import { FileSyncProgressRecord } from '../model/file-sync-progress.model';
import { TreatmentDesign, TreatmentDesigns } from '../model';
import { TreatmentFile, TreatmentFileKind, TreatmentFiles } from '../model/treatment-file.model';
import { TreatmentNote } from '../model/treatment-note.model';
import { TreatmentType } from '../model/treatmentType';
import { TreatmentStatus } from '../model/treatment-status.model';
import { Base64ImageExtractService } from '../service/base64-image-extractor.service';
import { isString, omitBy, sortBy } from 'lodash-es';
import { TreatmentPendingStatusFilter } from '../model/treatment-pending-status-filter.enum';
import { RegenerateAiNightGuardInputModel } from '../model/regenerate-ai-night-guard-input-model';
import { BulkDeliveryResult, TreatmentBulkDeliveryInspection } from '../model/bulk-delivery';
import { TreatmentDesignNoteEditLogs } from '../model/treatment-design-note-edit-log';
import { ExportDoctorRevisionsParams, ListDesignerRevisionsParams, ListTreatmentsCountsParams } from './models';
import { DesignerRevisions, RevisionDesign, TreatmentsCount } from '../model';
import { ListRevisionDesignsParams } from './models';
import { parseISO } from 'date-fns';
import { Member } from '../model/member.model';
import {
    TreatmentDesignQCChecklistResponse,
    TreatmentDesignQCChecklistEditModel,
} from '../../components/qc-checklist/model';
import { RequiredDesignFileInspection } from '../model/required-design-file-template';

const basePath = '/api/treatments';

@Injectable({ providedIn: 'root' })
export class TreatmentClient {
    /** Starts a Retainer job using the original order scans. */
    enqueueAiRetainer(treatmentId: string): Observable<RetainerAiJobSubmission> {
        return this._client.post<RetainerAiJobSubmission>(`${basePath}/${treatmentId}:ai-retainer-enqueue`, null);
    }

    /** Creates a new Retainer job and reports the combined scan upload percentage when available. */
    regenerateAiRetainer(
        treatmentId: string,
        upper: File,
        lower: File,
        onProgress?: ((progress: number) => void) | null
    ): Observable<RetainerAiJobSubmission> {
        const form = new FormData();
        form.append('upper', upper);
        form.append('lower', lower);
        return this._client
            .post<RetainerAiJobSubmission>(`${basePath}/${treatmentId}:regenerate-ai-retainer`, form, {
                observe: 'events',
                reportProgress: true,
            })
            .pipe(
                tap(event => {
                    if (event.type === HttpEventType.UploadProgress && event.total != null && event.total > 0) {
                        onProgress?.(Math.min(100, Math.floor((event.loaded * 100) / event.total)));
                    } else if (event.type === HttpEventType.ResponseHeader) {
                        onProgress?.(100);
                    }
                }),
                filter(
                    (event): event is HttpResponse<RetainerAiJobSubmission> => event.type === HttpEventType.Response
                ),
                map(event => {
                    if (event.body === null) throw new Error('Retainer submission returned an empty response.');
                    return event.body;
                })
            );
    }

    /** Lists accessible patient orders using a JSON request body. */
    getPatientRelatedTreatments(
        request: GetPatientRelatedTreatmentsRequest
    ): Observable<PageModel<PatientTreatmentRecord>> {
        return this._client.post<PageModel<PatientTreatmentRecord>>('/api/treatments/related-treatments', request);
    }

    private readonly _client = inject(HttpClient);
    private readonly _base64ImageExtractor = inject(Base64ImageExtractService);

    list(params: ListPagedTreatmentQuery = {}): Observable<PageModel<Treatment>> {
        const p: HttpParamsOptions['fromObject'] = {};
        for (const key in params) {
            const v = params[key as keyof ListPagedTreatmentQuery];
            if (v != null) p[key] = v instanceof Date ? v.toISOString() : v;
        }
        return this._client.get<PageModel<Treatment>>(basePath, {
            params: new HttpParams({ fromObject: p }),
        });
    }

    countPendingItems(pendingType: TreatmentPendingStatusFilter) {
        return this._client.get<ItemsCount>(`${basePath}:countByPendingStatus:${pendingType}`).pipe(
            map<ItemsCount, PageModel<Treatment>>(({ value }) => ({
                total: value,
                pageSize: 1,
                pageIndex: 1,
                items: [],
            })),
            catchError(() => of<PageModel<Treatment>>({ items: [], total: -1, pageSize: 1, pageIndex: 1 }))
        );
    }

    countPendingAnswerTreatments() {
        return this.countPendingItems(TreatmentPendingStatusFilter.pendingAnswer);
    }

    detail(treatmentId: string) {
        return this._client.get<TreatmentDetail>(`${basePath}/${treatmentId}/detail`);
    }

    getRevisionInsights(treatmentId: string) {
        return this._client.get<RevisionInsights>(`${basePath}/${treatmentId}/revision-insights`);
    }

    getTags(treatmentId: string) {
        return this._client.get<TreatmentTag[]>(`${basePath}/${treatmentId}/tags`);
    }

    getPatientFileSyncProgresses(fileGuid: string[]): Observable<FileSyncProgressRecord> {
        return this._client.get<FileSyncProgressRecord>(`${basePath}/patient-files/sync-progress`, {
            params: fileGuid.reduce((p, c) => p.append('fileGuid', c), new HttpParams()),
        });
    }

    getPatientFileDownloadLinks(fileGuid: string[]) {
        return this._client.get<Record<string, string>>(`${basePath}/patient-files/download-links`, {
            params: fileGuid.reduce((p, c) => p.append('fileGuid', c), new HttpParams()),
        });
    }

    getDesignFileDownloadLinks(treatmentId: string, designId: number, fileGuid: string[]) {
        return this._client.get<Record<string, string>>(
            `${basePath}/${treatmentId}/designs/${designId}/files/download-links`,
            {
                params: fileGuid.reduce((p, c) => p.append('fileGuid', c), new HttpParams()),
            }
        );
    }

    /**
     * Resolves download links to STL previews of the given patient files, generating any STL that
     * does not exist yet. The source files stay unchanged and remain downloadable. Files that need
     * no conversion, or whose conversion failed, are absent from the response.
     */
    ensurePatientFilePreviewStlLinks(fileGuid: string[]): Observable<Record<string, string>> {
        return this._client.post<Record<string, string>>(
            `${basePath}/patient-files:ensure-preview-stl-links`,
            fileGuid.reduce((acc, c) => {
                acc.append('fileGuid', c);
                return acc;
            }, new FormData())
        );
    }

    /**
     * Resolves download links to STL previews of the given design files, generating any STL that
     * does not exist yet. The source files stay unchanged and remain downloadable. Files that need
     * no conversion, or whose conversion failed, are absent from the response.
     */
    ensureDesignFilePreviewStlLinks(
        treatmentId: string,
        designId: number,
        fileGuid: string[]
    ): Observable<Record<string, string>> {
        return this._client.post<Record<string, string>>(
            `${basePath}/${treatmentId}/designs/${designId}/files:ensure-preview-stl-links`,
            fileGuid.reduce((acc, c) => {
                acc.append('fileGuid', c);
                return acc;
            }, new FormData())
        );
    }

    retrySyncPatientFile(...fileGuid: string[]) {
        return this._client.post(
            `${basePath}/patient-files:trigger-sync`,
            fileGuid.reduce((acc, c) => {
                acc.append('fileGuid', c);
                return acc;
            }, new FormData())
        );
    }

    createDesign(treatmentId: string, revisionId?: string | null): Observable<TreatmentDesign> {
        const formData = new FormData();
        if (revisionId) formData.append('revisionId', revisionId);
        return this._client.post<TreatmentDesign>(`${basePath}/${treatmentId}/designs`, formData);
    }

    listDesigns(treatmentId: string): Observable<TreatmentDesigns> {
        return this._client.get<TreatmentDesigns>(`${basePath}/${treatmentId}/designs`).pipe(
            map(designs => {
                return designs.map(design => ({
                    ...design,
                    files: sortBy(
                        design.files.map(f => ({
                            ...f,
                            createdDate: isString(f.createdDate) ? parseISO(f.createdDate) : f.createdDate,
                            modifiedDate: isString(f.modifiedDate) ? parseISO(f.modifiedDate) : f.modifiedDate,
                        })),
                        x => (x.fileKind === TreatmentFileKind.Engineering ? Number.MAX_VALUE : x.id)
                    ),
                }));
            })
        );
    }

    removeDesignFile(treatmentId: string, designId: number, fileGuid: string) {
        return this._client.delete<void>(`${basePath}/${treatmentId}/designs/${designId}/files/${fileGuid}`);
    }

    removeAllDesignFiles(treatmentId: string, designId: number) {
        return this._client.delete<void>(`${basePath}/${treatmentId}/designs/${designId}/all-files`);
    }

    assignReviewer(treatmentId: string, designId: number, reviewerId: string): Observable<void> {
        const body = new FormData();
        body.append('reviewerId', reviewerId);
        return this._client.put<void>(`${basePath}/${treatmentId}/designs/${designId}/reviewer`, body);
    }

    requestReview(treatmentId: string, designId: number) {
        return this._client.put<void>(`${basePath}/${treatmentId}/designs/${designId}/review/status:requestReview`, {});
    }

    inspectRequiredDesignFiles(treatmentId: string, designId: number): Observable<RequiredDesignFileInspection> {
        return this._client.get<RequiredDesignFileInspection>(
            `${basePath}/${treatmentId}/designs/${designId}/required-files/inspection`
        );
    }

    putDesignNote(treatmentId: string, designId: number, value: string) {
        const body = new FormData();
        body.append('designNote', value.slice(0, 5000));
        return this._client.put<void>(`${basePath}/${treatmentId}/designs/${designId}/note`, body);
    }

    listDesignNoteEditLogs(treatmentId: string, designId: number) {
        return this._client.get<TreatmentDesignNoteEditLogs>(
            `${basePath}/${treatmentId}/designs/${designId}/note/edit-logs`
        );
    }

    acceptReview(treatmentId: string, designId: number, reviewId: number, message: string) {
        const body = new FormData();
        body.append('reviewId', `${reviewId}`);
        body.append('message', `${message}`);
        return this._client.put<void>(`${basePath}/${treatmentId}/designs/${designId}/review:accept`, body);
    }

    rejectReview(treatmentId: string, designId: number, reviewId: number, reason: string) {
        const body = new FormData();
        body.append('reviewId', `${reviewId}`);
        return from(reason ? this._base64ImageExtractor.extract(reason) : of('')).pipe(
            switchMap(r => {
                body.append('reason', r);
                return this._client.put<void>(`${basePath}/${treatmentId}/designs/${designId}/review:reject`, body);
            })
        );
    }

    putIsDesignUnread(treatmentId: string, designId: number, isUnread: boolean) {
        const body = new FormData();
        body.append('isUnread', `${isUnread}`);
        return this._client.put<void>(`${basePath}/${treatmentId}/designs/${designId}/isUnread`, body);
    }

    markDesignRead(treatmentId: string, designId: number) {
        return this.putIsDesignUnread(treatmentId, designId, false);
    }

    listTreatmentFilesByFileType(treatmentId: string, designId: number, fileType: number): Observable<TreatmentFiles> {
        return this._client.get<TreatmentFiles>(`${basePath}/${treatmentId}/designs/${designId}/files/${fileType}`);
    }

    listNotes(treatmentId: string): Observable<TreatmentNote[]> {
        return this._client.get<TreatmentNote[]>(`${basePath}/${treatmentId}/notes`);
    }

    bulkListNotes(treatmentIds: string[]) {
        return this._client.post<Record<string, TreatmentNote[]>>(`${basePath}/notes:bulkList`, treatmentIds);
    }

    saveNote(treatmentId: string, noteId: number, body: { content: string }) {
        return from(this._base64ImageExtractor.extract(body.content)).pipe(
            switchMap(content => {
                body.content = content;
                return this._client.post<TreatmentNote>(`${basePath}/${treatmentId}/notes/${noteId}`, body);
            })
        );
    }

    exportCsv(params: ListPagedTreatmentQuery = {}) {
        const p: HttpParamsOptions['fromObject'] = {};
        for (const key in params) {
            const v = params[key as keyof ListPagedTreatmentQuery];
            if (v != null) p[key] = v instanceof Date ? v.toISOString() : v;
        }
        return this._client.get(`${basePath}:exportCsv`, {
            responseType: 'blob',
            params: new HttpParams({
                fromObject: p,
            }),
        });
    }

    listTreatmentTypes(): Observable<TreatmentType[]> {
        return this._client.get<TreatmentType[]>(`/api/treatments/types`);
    }

    markRedesign(treatmentId: string, requirement?: string | null) {
        const form = new FormData();
        return from(requirement ? this._base64ImageExtractor.extract(requirement) : of(undefined)).pipe(
            switchMap(r => {
                if (!!r) form.append('requirement', r);
                return this._client.post<void>(`${basePath}/${treatmentId}:markRedesign`, form);
            })
        );
    }

    onPatientFilesBulkDownloaded(treatmentIds: string[]) {
        return this._client.post<Record<string, TreatmentStatus | null | undefined>>(
            `${basePath}/patient-files:bulk-downloaded`,
            treatmentIds
        );
    }

    listStatuses() {
        return this._client.get<TreatmentStatus[]>(`${basePath}/statuses`);
    }

    getRedesignRequirement(treatmentId: string) {
        return this._client.get(`${basePath}/${treatmentId}/redesignRequirement`, { responseType: 'text' });
    }

    getReviewRejectReason(treatmentId: string, reviewId: number): Observable<string> {
        return this._client.get(`${basePath}/${treatmentId}/reviews/${reviewId}/rejectReason`, {
            responseType: 'text',
        });
    }

    regenerateAiNightGuard(treatmentId: string, model: RegenerateAiNightGuardInputModel) {
        if (!model.upper || !model.lower) throw new Error('Upper and lower scans are required!');

        const formData = new FormData();
        formData.set('upper', model.upper);
        formData.set('lower', model.lower);

        if (model.undercutDegree != null) {
            formData.set('undercutDegree', `${model.undercutDegree}`);
        }

        return this._client.post(`${basePath}/${treatmentId}:regenerate-ai-night-guard`, formData);
    }

    enqueueAiCrown(treatmentId: string) {
        return this._client.post(`${basePath}/${treatmentId}:ai-crown-enqueue`, null);
    }

    createAiCadLaunchSession(treatmentId: string, designId: number): Observable<AiCadLaunchSession> {
        return this._client.post<AiCadLaunchSession>(
            `${basePath}/${treatmentId}/ai-crown-cad/designs/${designId}/launch-session`,
            null
        );
    }

    inspectBulkDelivery(treatmentId: string[]) {
        return this._client.post<Record<string, TreatmentBulkDeliveryInspection>>(
            `${basePath}:inspect-bulk-delivery`,
            treatmentId.reduce((prev, c) => {
                prev.append('treatmentIds', c);
                return prev;
            }, new FormData())
        );
    }

    deliveryDesign(treatmentId: string, data: { messageBody: string; batchId?: string }) {
        return this._client.post<BulkDeliveryResult>(`${basePath}/${treatmentId}:delivery-design`, data);
    }

    handleStuckProgress(treatmentId: string) {
        return this._client.patch<void>(`${basePath}/${treatmentId}/deliveries:handle-stuck-progress`, {});
    }

    listDesignerRevisions(params: ListDesignerRevisionsParams) {
        return this._client
            .get<DesignerRevisions>(`${basePath}/statistics/designer-revisions`, {
                params: omitBy(
                    {
                        startDate: params.startDate?.toISOString() ?? '',
                        endDate: params.endDate?.toISOString() ?? '',
                    },
                    x => !x
                ),
            })
            .pipe(
                map(items =>
                    items.map(i => ({
                        ...i,
                        dataKey: `${i.designerId}-${i.treatmentTypeOriginalId}`,
                    }))
                )
            );
    }

    exportDoctorRevisions(params: ExportDoctorRevisionsParams) {
        const p: HttpParamsOptions['fromObject'] = {};
        for (const key in params) {
            const v = params[key as keyof ExportDoctorRevisionsParams];
            if (v != null) p[key] = v instanceof Date ? v.toISOString() : v;
        }
        return this._client.get(`${basePath}/statistics:exportDoctorRevisions`, {
            responseType: 'blob',
            params: new HttpParams({
                fromObject: p,
            }),
        });
    }

    listRevisionDesigns(params: ListRevisionDesignsParams) {
        return this._client.get<PageModel<RevisionDesign>>(`${basePath}/revision-designs`, {
            params: new HttpParams({
                fromObject: omitBy(
                    {
                        startDate: params.startDate?.toISOString() ?? '',
                        endDate: params.endDate?.toISOString() ?? '',
                        designerId: params.designerId ?? '',
                        liability: params.liability ?? '',
                        pageIndex: params.pageIndex ?? '',
                        pageSize: params.pageSize ?? '',
                    },
                    x => !x
                ),
            }),
        });
    }

    listTreatmentsCounts(params: ListTreatmentsCountsParams) {
        return this._client
            .get<TreatmentsCount[]>(`${basePath}/statistics/treatments-counts`, {
                params: omitBy(
                    {
                        startDate: params.startDate?.toISOString() ?? '',
                        endDate: params.endDate?.toISOString() ?? '',
                        interval: params.interval ?? '',
                    },
                    x => !x
                ),
            })
            .pipe(map(items => items.map(i => ({ ...i, date: parseISO((i as any).date) }))));
    }

    assignDesigner(treatmentId: string, designerId: string) {
        const body = new FormData();
        body.set('designerId', designerId);
        return this._client.put<Member>(`${basePath}/${treatmentId}:assign-designer`, body);
    }

    /**
     * Get the QC checklist for a treatment design.
     * @param treatmentId - The treatment ID
     * @param designId - The design ID
     * @returns The saved QC checklist or an empty items array
     */
    getDesignChecklist(treatmentId: string, designId: number): Observable<TreatmentDesignQCChecklistResponse> {
        return this._client
            .get<TreatmentDesignQCChecklistResponse>(`${basePath}/${treatmentId}/designs/${designId}/checklist`)
            .pipe(catchError(() => of({ items: [] })));
    }

    /**
     * Save the QC checklist for a treatment design.
     * @param treatmentId - The treatment ID
     * @param designId - The design ID
     * @param checklist - The checklist data to save
     */
    saveDesignChecklist(
        treatmentId: string,
        designId: number,
        checklist: TreatmentDesignQCChecklistEditModel
    ): Observable<void> {
        return this._client.post<void>(`${basePath}/${treatmentId}/designs/${designId}/checklist`, checklist);
    }
}
